import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cancelPendingAutoSync } from "@/lib/autoSync";
import {
  Clock, RefreshCw, Mail, XCircle,
  CheckCircle2, Wifi, WifiOff, UploadCloud, Server, ShieldCheck
} from "lucide-react";

const API = '/api';

export default function RelayControl() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [held, setHeld] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [holdSeconds, setHoldSeconds] = useState(120);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [relayConfig, setRelayConfig] = useState(null);
  const [relayUrlDraft, setRelayUrlDraft] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/relay/status`).catch(() => ({ data: { status: "unreachable" } })),
      axios.get(`${API}/relay/stats`).catch(() => ({ data: {} })),
      axios.get(`${API}/relay/held`).catch(() => ({ data: { held_emails: [] } })),
      axios.get(`${API}/relay/config`).catch(() => ({ data: { configured: false, relay_url: "", source: "unconfigured" } })),
    ]).then(([s, st, h, c]) => {
      setStatus(s.data);
      setStats(st.data);
      setHeld(h.data.held_emails || []);
      setHoldSeconds(s.data.hold_seconds || 120);
      setRelayConfig(c.data);
      setRelayUrlDraft((c.data.tenant_relay_url || c.data.relay_url || ""));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
  }, [refresh]);

  const saveRelayUrl = async () => {
    setSavingUrl(true);
    try {
      await axios.put(`${API}/relay/config/relay-url`, { relay_url: relayUrlDraft.trim() });
      toast.success(relayUrlDraft.trim() ? "VPS relay URL saved" : "VPS relay URL cleared");
      refresh();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
    setSavingUrl(false);
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
  }, [refresh]);

  const toggleRelay = async (enabled) => {
    setToggling(true);
    try {
      await axios.post(`${API}/relay/config`, { relay_enabled: enabled });
      toast.success(`Relay ${enabled ? "ENABLED" : "DISABLED"}`);
      refresh();
    } catch { toast.error("Failed to update relay"); }
    setToggling(false);
  };

  const updateHold = async () => {
    try {
      await axios.post(`${API}/relay/config`, { hold_seconds: holdSeconds });
      toast.success(`Hold time updated to ${holdSeconds}s`);
      refresh();
    } catch { toast.error("Failed to update"); }
  };

  const recallEmail = async (holdId) => {
    try {
      const r = await axios.post(`${API}/relay/recall`, { hold_id: holdId });
      if (r.data.success) { toast.success("Email recalled"); refresh(); }
      else toast.error(r.data.error || "Recall failed");
    } catch { toast.error("Recall failed"); }
  };

  const pushSync = async (clearImageCache = false) => {
    cancelPendingAutoSync(); // avoid a double-sync if user just edited something
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/relay/sync`, { clear_image_cache: clearImageCache });
      if (r.data.ok) {
        const c = r.data.pushed || {};
        const summary = Object.entries(c).map(([k, v]) => `${k}:${v}`).join(" · ");
        toast.success(`Pushed to relay → ${summary}`);
        setLastSync(new Date().toLocaleTimeString());
        refresh();
      } else {
        toast.error(`Sync failed: ${r.data.error || r.data.status_code}`);
      }
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    }
    setSyncing(false);
  };

  const relayEnabled = status?.relay_enabled;
  const isHealthy = status?.status === "healthy";
  const isUnreachable = status?.status === "unreachable";

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="relay-control-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Relay Control</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Monitor and control the SMTP signature relay.</p>
        </div>
        <Button onClick={refresh} variant="outline" className="rounded-sm text-xs" data-testid="refresh-btn">
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* ── Main Toggle ── */}
      <div className={`border-2 rounded-sm p-6 ${isUnreachable ? "border-[#C8C6C4] bg-[#F3F2F1]" : relayEnabled ? "border-[#107C10] bg-[#DFF6DD]" : "border-[#D83B01] bg-[#FDE7E9]"}`}
        data-testid="relay-toggle-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isUnreachable ? <WifiOff size={32} className="text-[#C8C6C4]" /> :
              relayEnabled ? <Wifi size={32} className="text-[#107C10]" /> : <WifiOff size={32} className="text-[#D83B01]" />}
            <div>
              <h2 className="text-lg font-semibold text-[#171717]">
                {isUnreachable ? "Relay Unreachable" : relayEnabled ? "Relay ACTIVE" : "Relay DISABLED"}
              </h2>
              <p className="text-xs text-[#5E5E5E]">
                {isUnreachable ? "Cannot connect to VPS — check if relay container is running"
                  : relayEnabled ? "Processing outbound mail and injecting signatures"
                  : "Bypassed — Exchange delivers mail directly without signatures"}
              </p>
            </div>
          </div>
          {!isUnreachable && (
            <button onClick={() => toggleRelay(!relayEnabled)} disabled={toggling}
              className={`w-16 h-8 rounded-full relative transition-colors ${relayEnabled ? "bg-[#107C10]" : "bg-[#C8C6C4]"}`}
              data-testid="relay-power-toggle">
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${relayEnabled ? "left-[34px]" : "left-1"}`} />
            </button>
          )}
        </div>
      </div>

      {/* ── Status Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <StatusCard icon={<CheckCircle2 size={16} />} label="Health" value={isHealthy ? "Healthy" : isUnreachable ? "Offline" : "Unhealthy"}
          color={isHealthy ? "#107C10" : "#A4262C"} />
        <StatusCard icon={<Mail size={16} />} label="Processed" value={stats?.total_processed ?? "—"} color="#0F6CBD" />
        <StatusCard icon={<Clock size={16} />} label="Held Now" value={held.length} color="#D83B01" />
        <StatusCard icon={<XCircle size={16} />} label="Recalled" value={stats?.recalled ?? "—"} color="#5C2D91" />
      </div>

      {/* ── DKIM signing status ── */}
      {!isUnreachable && (
        <div className={`mt-4 border rounded-sm p-4 text-sm ${
          status?.dkim_signing
            ? "border-[#107C10] bg-[#DFF6DD]"
            : "border-[#FFD13C] bg-[#FFF4CE]"
        }`} data-testid="dkim-status-card">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className={status?.dkim_signing ? "text-[#107C10]" : "text-[#8A6116]"} />
            <div className="flex-1">
              <div className="font-medium text-[#171717]">
                DKIM signing: {status?.dkim_signing ? "ON" : "OFF"}
              </div>
              <p className="text-xs text-[#5E5E5E] mt-0.5">
                {status?.dkim_signing
                  ? "Outbound mail is being signed with your domain DKIM key — strict spam filters (SpamExperts, Mimecast) will trust it."
                  : "Outbound mail is NOT being DKIM-signed. Strict spam filters may block deliveries. Run scripts/generate_dkim_keys.sh on the VPS, publish the DNS record, and rebuild the container."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── VPS Relay URL Configuration ── */}
      <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm p-5" data-testid="relay-url-card">
        <div className="flex items-start gap-3">
          <Server size={20} className="text-[#5C2D91] mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-[#171717]">VPS Relay endpoint</h3>
            <p className="text-xs text-[#5E5E5E] mt-0.5">
              The HTTP base URL where this dashboard pushes templates and reads relay health.
              Stored per-tenant in the database — each environment (preview / production)
              should point to its own VPS to prevent data contamination across environments.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Input
                placeholder="e.g. http://185.69.149.244/relay-api"
                value={relayUrlDraft}
                onChange={(e) => setRelayUrlDraft(e.target.value)}
                className="text-xs font-mono max-w-xl"
                data-testid="relay-url-input"
              />
              <Button
                onClick={saveRelayUrl}
                disabled={savingUrl}
                className="bg-[#0F6CBD] hover:bg-[#0A57A0] rounded-sm text-xs"
                data-testid="relay-url-save"
              >
                {savingUrl ? "Saving…" : "Save"}
              </Button>
            </div>
            {relayConfig && (
              <p className="text-[10px] text-[#5E5E5E] mt-2" data-testid="relay-url-source">
                Effective:{" "}
                <span className="font-mono">{relayConfig.relay_url || "— not set —"}</span>
                {" · "}
                Source:{" "}
                <span className={`font-semibold ${
                  relayConfig.source === "database" ? "text-[#107C10]" :
                  relayConfig.source === "environment" ? "text-[#0F6CBD]" :
                  "text-[#A4262C]"
                }`}>
                  {relayConfig.source}
                </span>
                {relayConfig.source === "unconfigured" && " — Push to Relay will fail until you set this"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Sync to Relay ── */}
      <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm p-5" data-testid="sync-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <UploadCloud size={20} className="text-[#0F6CBD] mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-[#171717]">Push templates &amp; data to Relay</h3>
              <p className="text-xs text-[#5E5E5E] mt-0.5 max-w-xl">
                Sends the latest templates, components, brands, rules, users and campaigns from this
                dashboard to the VPS relay. Run this after editing a template if you don&rsquo;t see
                changes in delivered emails.
              </p>
              {lastSync && <p className="text-[10px] text-[#5E5E5E] mt-1">Last push: {lastSync}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              onClick={() => pushSync(false)}
              disabled={syncing || isUnreachable}
              className="bg-[#0F6CBD] hover:bg-[#0B5BA0] text-white rounded-sm text-xs"
              data-testid="push-sync-btn"
            >
              {syncing ? "Pushing…" : "Push to Relay"}
            </Button>
            <Button
              onClick={() => pushSync(true)}
              disabled={syncing || isUnreachable}
              variant="outline"
              className="rounded-sm text-[10px]"
              data-testid="push-sync-clear-btn"
            >
              Push + clear image cache
            </Button>
          </div>
        </div>
      </div>

      {/* ── Hold Settings ── */}
      <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm p-5" data-testid="hold-settings">
        <h3 className="text-sm font-medium text-[#171717] mb-3">Hold Queue Settings</h3>
        <div className="flex items-center gap-3">
          <label className="text-xs text-[#5E5E5E] shrink-0">Hold time (seconds):</label>
          <input type="number" min="0" max="600" value={holdSeconds}
            onChange={e => setHoldSeconds(parseInt(e.target.value) || 0)}
            className="w-20 text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 text-center"
            data-testid="hold-seconds-input" />
          <Button onClick={updateHold} size="sm" className="bg-[#0F6CBD] text-white rounded-sm text-xs" data-testid="save-hold-btn">
            Save
          </Button>
          <span className="text-[10px] text-[#5E5E5E] ml-2">
            {holdSeconds === 0 ? "Hold disabled — emails forward immediately" : `Emails held for ${holdSeconds}s before delivery`}
          </span>
        </div>
      </div>

      {/* ── Held Emails (Outbox) ── */}
      {held.length > 0 && (
        <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm" data-testid="held-emails">
          <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center gap-2">
            <Clock size={14} className="text-[#D83B01]" />
            <h3 className="text-sm font-medium text-[#171717]">Held Emails — Outbox</h3>
          </div>
          <div className="divide-y divide-[#F3F2F1]">
            {held.map(email => (
              <div key={email.hold_id} className="px-5 py-3 flex items-center justify-between" data-testid={`held-${email.hold_id}`}>
                <div>
                  <p className="text-xs font-medium text-[#171717]">{email.subject || "(no subject)"}</p>
                  <p className="text-[10px] text-[#5E5E5E]">
                    From: {email.mail_from} &middot; Mode: {email.mode} &middot; Held since: {new Date(email.created_at).toLocaleTimeString()}
                  </p>
                </div>
                <Button onClick={() => recallEmail(email.hold_id)} size="sm" variant="outline"
                  className="text-xs text-[#A4262C] border-[#A4262C]/30 hover:bg-[#FDE7E9] rounded-sm"
                  data-testid={`recall-${email.hold_id}`}>
                  Recall
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent Activity ── */}
      {stats?.recent?.length > 0 && (
        <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm" data-testid="recent-activity">
          <div className="px-5 py-3 border-b border-[#E1DFDD]">
            <h3 className="text-sm font-medium text-[#171717]">Recent Activity</h3>
          </div>
          <div className="divide-y divide-[#F3F2F1]">
            {stats.recent.map((log, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center gap-3 text-xs">
                <span className={`w-2 h-2 rounded-full shrink-0 ${log.status === "processed" ? "bg-[#107C10]" : log.status === "skipped" ? "bg-[#C8C6C4]" : "bg-[#D83B01]"}`} />
                <span className="text-[#5E5E5E] w-28 shrink-0">{log.from?.split("@")[0] || "?"}</span>
                <span className="text-[#171717] flex-1 truncate">{log.subject || "(no subject)"}</span>
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${log.mode === "FULL" ? "bg-[#0F6CBD]/10 text-[#0F6CBD]" : log.mode === "SHORT" ? "bg-[#D83B01]/10 text-[#D83B01]" : "bg-[#F3F2F1] text-[#5E5E5E]"}`}>
                  {log.mode || "SKIP"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusCard({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E1DFDD] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-1">
        <span style={{ color }}>{icon}</span>
        <span className="text-[10px] uppercase font-semibold text-[#5E5E5E]">{label}</span>
      </div>
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}
